/* eslint-disable @typescript-eslint/no-explicit-any */
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import { Box, Paper, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { forwardRef } from 'react';
import TemplateText from 'src/components/TemplateText/TemplateText';
import { formatUnicodeStylesToSafeHtml } from 'src/components/TemplateText/helpers';
import { InboxContactChatMessage } from 'src/constants/types';
import { Button } from 'src/UIKit';
import { ChatMessageText } from '../ChatMessageText';
import { ChatScheduledMessagePanel } from './ChatScheduledMessagePanel.styles';
import { CompactIconButton } from './CompactIconButton.styles';
import { VerticalScrollArea } from '../VerticalScrollArea';
import { useChatController } from '../../controllers';
import { ChatProps } from '../../types';
import ChatFooter from '../ChatFooter';
import ChatHeader from '../ChatHeader';
import ContactsSearch from '../ContactsSearch';
import Conversation from '../Conversation';

const Chat = forwardRef(function Chat(props: ChatProps, ref) {
  const {
    messages,
    contact,
    customFields,
    markMessageAsUnread,
    startReschedulingScheduledMessage,
    scheduledMessageToEdit,
    clearScheduledMessageToEdit,
    updateScheduledMessage,
    addMessage,
    updateLinks,
    isChatLoading,
    isNewChat,
    onSelectContact,
    setIsChatLoading,
    refreshContacts,
    messageTemplates,
    groups,
    group,
    isCompanyInSandBoxMode,
    isCompanyHasSubscription,
    showErrorDetails,
    hideDemoFails,
    users,
    newMessage,
    scheduledMessages,
    openEditContact,
    isMobile,
    isContentReady = false,
    onBackToList,
    hasMoreMessages = false,
    isLoadingMoreMessages = false,
  } = props;

  const {
    ConfirmationDialog,
    defaultCustomFields,
    showScheduledMessages,
    setShowScheduledMessages,
    inlineEditingMessageId,
    inlineEditingText,
    setInlineEditingText,
    isInlineUpdating,
    handleScroll,
    handleKeyboardFocus,
    confirmCancelScheduledMessage,
    startInlineEditing,
    cancelInlineEditing,
    getInlineEditState,
    saveInlineEditing,
  } = useChatController(props, ref);

  if (isNewChat) {
    return (
      <Paper square elevation={1}>
        <Stack height={isMobile ? 'calc(var(--vh, 1vh) * 89)' : '85vh'} overflow="hidden">
          <ContactsSearch
            refreshContacts={refreshContacts}
            group={group}
            setIsChatLoading={setIsChatLoading}
            onSelectContact={onSelectContact}
            isMobile={isMobile}
            onBackToList={onBackToList}
          />
        </Stack>
      </Paper>
    );
  }

  if (contact === undefined) {
    return (
      <Paper square elevation={1}>
        <Stack height="85vh" alignItems="center" justifyContent="center">
          {isChatLoading === undefined && (
            <Typography variant="body1" color="text.secondary">Select Chat</Typography>
          )}
        </Stack>
      </Paper>
    );
  }

  const height = isMobile ? 'calc(var(--vh, 1vh) * 100)' : '85vh';
  const scrollHeight = isMobile ? 'calc(var(--vh, 1vh) * 100 - 200px)' : '85vh';
  const mobileTransition = isMobile
    ? 'height 0.2s ease-in-out, max-height 0.2s ease-in-out'
    : 'none';

  return (
    <Paper square elevation={1}>
      <Stack
        height={height}
        maxHeight={height}
        width="100%"
        overflow="hidden"
        position="relative"
        sx={{ transition: mobileTransition }}
      >
        <ConfirmationDialog />
        <Box flexShrink={0} zIndex={10} bgcolor="grey.50" pt="env(safe-area-inset-top)">
          <ChatHeader
            openEditContact={openEditContact}
            refreshContacts={refreshContacts}
            users={users}
            contact={contact}
            groups={groups}
            customFields={customFields}
            isMobile={isMobile}
            onBackToList={onBackToList}
          />
        </Box>

        <VerticalScrollArea
          ref={ref as any}
          data-conversation-area="true"
          onScroll={handleScroll}
          width="100%"
          position="relative"
          flexGrow={1}
          minHeight={0}
          height={scrollHeight}
          maxHeight={scrollHeight}
          bgcolor="background.default"
          sx={{ WebkitOverflowScrolling: 'touch', transition: mobileTransition }}
        >
          <Conversation
            isLoadingMoreMessages={isLoadingMoreMessages}
            hasMoreMessages={hasMoreMessages}
            markMessageAsUnread={markMessageAsUnread}
            messages={messages}
            isContentReady={isContentReady}
            showErrorDetails={showErrorDetails}
            hideDemoFails={hideDemoFails}
          />
        </VerticalScrollArea>

        <Box flexShrink={0} bgcolor="background.default">
          {scheduledMessages.length > 0 && (
            <>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                px={2}
                py={1.25}
                borderTop={1}
                borderBottom={showScheduledMessages ? 1 : 0}
                borderColor="divider"
                bgcolor="grey.100"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <ScheduleSendIcon color="action" fontSize="small" />
                  <Typography variant="body2">
                    {scheduledMessages.length} message
                    {scheduledMessages.length === 1 ? '' : 's'} scheduled to be sent.
                  </Typography>
                </Stack>
                <Button size="small" variant="text" onClick={() => setShowScheduledMessages(prev => !prev)}>
                  {showScheduledMessages ? 'Hide scheduled messages' : 'See all scheduled messages'}
                </Button>
              </Box>

              {showScheduledMessages && (
                <VerticalScrollArea
                  maxHeight={isMobile ? '30vh' : 220}
                  px={2}
                  py={1.5}
                  borderBottom={1}
                  borderColor="divider"
                  bgcolor="grey.100"
                >
                  <Stack spacing={1}>
                    {scheduledMessages.map((scheduledMessage: InboxContactChatMessage) => {
                      const isInlineEditing = inlineEditingMessageId === scheduledMessage.id;
                      const isInlineSaveDisabled =
                        isInlineEditing && !getInlineEditState(scheduledMessage).canSave;

                      return (
                        <Box key={scheduledMessage.id} position="relative">
                          <ChatScheduledMessagePanel>
                            {scheduledMessage.status === 'pending' && !isInlineEditing && (
                              <Stack direction="row" spacing={0.5} position="absolute" top={6} right={6}>
                                <CompactIconButton
                                  size="small"
                                  title="Edit message"
                                  onClick={() => startInlineEditing(scheduledMessage)}
                                >
                                  <EditOutlinedIcon fontSize="inherit" />
                                </CompactIconButton>
                                <CompactIconButton
                                  size="small"
                                  title="Reschedule"
                                  onClick={() => startReschedulingScheduledMessage(scheduledMessage)}
                                >
                                  <AccessTimeOutlinedIcon fontSize="inherit" />
                                </CompactIconButton>
                                <CompactIconButton
                                  size="small"
                                  color="error"
                                  title="Cancel scheduled message"
                                  onClick={() => confirmCancelScheduledMessage(scheduledMessage)}
                                >
                                  <CloseIcon fontSize="inherit" />
                                </CompactIconButton>
                              </Stack>
                            )}
                            {isInlineEditing ? (
                              <TemplateText
                                text={inlineEditingText}
                                messageTemplates={messageTemplates}
                                customFields={defaultCustomFields}
                                disabled={isInlineUpdating}
                                updateText={setInlineEditingText}
                                showConfirmationText={false}
                                confirmationText=""
                                setFocus={true}
                                minHeight="72px"
                                padding="8px 8px 36px 8px"
                                hideAbsentDefaultValueAlert={true}
                              />
                            ) : (
                              <ChatMessageText
                                typography="body2"
                                pr={scheduledMessage.status === 'pending' ? 10 : 0}
                                dangerouslySetInnerHTML={{
                                  __html: formatUnicodeStylesToSafeHtml(scheduledMessage.text),
                                }}
                              />
                            )}
                            <Typography variant="caption" color="text.secondary" component="div" mt={0.75}>
                              Scheduled for{' '}
                              {dayjs(scheduledMessage.dateScheduled ?? scheduledMessage.dateCreated).format(
                                'YYYY-MM-DD hh:mm A',
                              )}
                            </Typography>
                            {isInlineEditing && (
                              <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
                                <Button size="small" variant="outlined" onClick={cancelInlineEditing}>Cancel</Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => saveInlineEditing(scheduledMessage)}
                                  disabled={isInlineSaveDisabled}
                                >
                                  {isInlineUpdating ? 'Saving...' : 'Save'}
                                </Button>
                              </Stack>
                            )}
                          </ChatScheduledMessagePanel>
                        </Box>
                      );
                    })}
                  </Stack>
                </VerticalScrollArea>
              )}
            </>
          )}

          <Box position="sticky" bottom={0} zIndex={10} bgcolor="background.paper">
            <ChatFooter
              isCompanyHasSubscription={isCompanyHasSubscription}
              isCompanyInSandBoxMode={isCompanyInSandBoxMode}
              messageTemplates={messageTemplates}
              selectedContactObject={contact}
              updateLinks={updateLinks}
              addMessage={addMessage}
              newMessage={newMessage}
              scheduledMessageToEdit={scheduledMessageToEdit}
              clearScheduledMessageToEdit={clearScheduledMessageToEdit}
              updateScheduledMessage={updateScheduledMessage}
              customFields={customFields}
              defaultCustomFields={defaultCustomFields}
              isMobile={isMobile}
              handleFocus={handleKeyboardFocus}
            />
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
});

export default Chat;
